import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { useThemeStore } from '@/stores/themeStore'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { getUserAvatarUrl } from '@/utils/avatarUtils'
import UserAvatar from '@/components/UserAvatar'
import BrandMark from '@/components/BrandMark'
import NewLookToggle from '@/components/NewLookToggle'
import {
  User,
  Settings,
  CreditCard,
  LogOut,
  Menu,
  X,
  Loader2,
  ChevronDown,
} from 'lucide-react'

import { getIconComponent, routeMapping } from '@/utils/iconMapping'


const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // Settings > "Collapsed Sidebar": desktop sidebar shrinks to an icon rail
  const collapsed = useThemeStore((state) => state.sidebarCollapsed)
  const [exitingImpersonation, setExitingImpersonation] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const desktopMenuRef = useRef(null)
  const mobileMenuRef = useRef(null)
  const location = useLocation()
  const navigate = useNavigate()
  const {
    user,
    logout,
    sideMenuItems,
    isLoadingSideMenu,
    companyUrl,
    companyDetails,
    fetchSideMenu,
    isImpersonating,
    impersonationMeta,
    exitImpersonation
  } = useAuthStore()
  const { userProfile } = useUserStore()
  const { animations } = useThemeStore()
  const { toast } = useToast()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      const inside = [desktopMenuRef.current, mobileMenuRef.current].some((node) => node && node.contains(e.target))
      if (!inside) setDropdownOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Fallback navigation if API menu is not available
  const fallbackNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: 'Home' },
    { name: 'Workouts', href: '/workouts', icon: 'Dumbbell' },
    { name: 'Nutrition', href: '/nutrition', icon: 'Apple' },
    { name: 'Progress', href: '/progress', icon: 'TrendingUp' },
    { name: 'Reports', href: '/reports', icon: 'BarChart3' },
    { name: 'Profile', href: '/profile', icon: 'User' },
    { name: 'Settings', href: '/settings', icon: 'Settings' },
  ]

  // Use API menu items if available, otherwise use fallback
  const navigation = sideMenuItems.length > 0 
    ? (() => {
        const apiNavItems = sideMenuItems
          .filter(item => item.show_in_menu === "1")
          .sort((a, b) => (a.sort || 0) - (b.sort || 0))
          .map(item => {
            const displayName = item.display_name || item.name || ''
            const normalizedName = typeof item.name === 'string' ? item.name : ''
            const fallbackRoute = normalizedName ? `/${normalizedName.toLowerCase().replace(/\s+/g, '-')}` : '/'
            const route = routeMapping[displayName] || fallbackRoute
            const isProgramBinder = displayName.toLowerCase() === 'program binder'
            const externalUrl = isProgramBinder ? null : item.menu_link || item.link || item.url
            
            return {
              name: displayName,
              href: isProgramBinder ? '/program-binder' : route,
              icon: item.icon || 'Home',
              id: item.id,
              rawItem: item,
              externalUrl
            }
          })
        
        const hasDashboard = apiNavItems.some(item => 
          item.name.toLowerCase() === 'dashboard' || 
          item.href === '/dashboard'
        )
        
        const dashboardItem = { name: 'Dashboard', href: '/dashboard', icon: 'Home', id: 'dashboard' }
        
        const filteredApiItems = apiNavItems.filter(item => 
          item.name.toLowerCase() !== 'dashboard' && 
          item.href !== '/dashboard'
        )
        
        const finalNavigation = [dashboardItem, ...filteredApiItems]
        const hasUploader = finalNavigation.some((item) => item.href === '/uploader')
        const hasBulkUpload = finalNavigation.some((item) => item.href === '/uploader?section=bulk-user-upload')
        const withoutLegacyUserUpload = finalNavigation.filter((item) => item.href !== '/userupload')
        if (!hasUploader || hasBulkUpload) return withoutLegacyUserUpload

        const uploaderIndex = withoutLegacyUserUpload.findIndex((item) => item.href === '/uploader')
        if (uploaderIndex === -1) return withoutLegacyUserUpload

        return [
          ...withoutLegacyUserUpload.slice(0, uploaderIndex + 1),
          ...withoutLegacyUserUpload.slice(uploaderIndex + 1),
        ]
      })()
    : fallbackNavigation

  const isConsultantUser = useMemo(() => {
    const roleSource = user?.role || user?.user_type
    const roleValue = typeof roleSource === 'string' ? roleSource.toLowerCase() : ''
    const consultantRole = roleValue.includes('consultant')
    const consultantFlag = Boolean(
      user?.is_consultant ||
      (Array.isArray(user?.roles) && user.roles.some((role) => {
        const name = typeof role?.name === 'string'
          ? role.name.toLowerCase()
          : typeof role?.display_name === 'string'
            ? role.display_name.toLowerCase()
            : ''
        return name.includes('consultant')
      }))
    )

    return consultantRole || consultantFlag
  }, [user])

  const isClientUser = useMemo(() => {
    const roleSource = user?.role || user?.user_type
    const roleValue = typeof roleSource === 'string' ? roleSource.toLowerCase() : ''
    const clientRole = roleValue.includes('client')
    const clientFlag = Boolean(
      (typeof user?.role?.name === 'string' && user.role.name.toLowerCase().includes('client')) ||
      (typeof user?.role?.display_name === 'string' && user.role.display_name.toLowerCase().includes('client')) ||
      (Array.isArray(user?.roles) && user.roles.some((role) => {
        const name = typeof role?.name === 'string'
          ? role.name.toLowerCase()
          : typeof role?.display_name === 'string'
            ? role.display_name.toLowerCase()
            : ''
        return name.includes('client')
      }))
    )

    return clientRole || clientFlag
  }, [user])

  const hasConsultantMenuAccess = useMemo(() => {
    return navigation.some((item) =>
      item.href === '/consultant-clients' ||
      item.href === '/exercise-plan-builder' ||
      item.href === '/consultant-notes'
    )
  }, [navigation])

  const hasClientOnlyMenu = useMemo(() => {
    const hasClientRoutes = navigation.some((item) => item.href.startsWith('/client'))
    return hasClientRoutes && !hasConsultantMenuAccess
  }, [navigation, hasConsultantMenuAccess])

  const navigationItems = useMemo(() => {
    const filteredNavigation = navigation.filter(item => item.href !== '/consultant-notes')
    if (hasClientOnlyMenu) return filteredNavigation
    if (!isConsultantUser && !hasConsultantMenuAccess) return filteredNavigation
    const hasConsultantNotes = filteredNavigation.some(item => item.href === '/consultant-notes')
    if (hasConsultantNotes) return filteredNavigation
    return [...filteredNavigation, { name: 'Consultant Notes', href: '/consultant-notes', icon: 'notes', id: 'consultant-notes' }]
  }, [navigation, isConsultantUser, hasConsultantMenuAccess, hasClientOnlyMenu])
  const navigationWithNotes = navigation.some(item => item.href === '/consultant-notes')
    ? navigation
    : [...navigation, { name: 'Consultant Notes', href: '/consultant-notes', icon: 'notes', id: 'consultant-notes' }]

  // Fetch side menu if not loaded and we have company URL
  useEffect(() => {
    if (companyUrl && sideMenuItems.length === 0 && !isLoadingSideMenu) {
      fetchSideMenu(companyUrl)
    }
  }, [companyUrl, sideMenuItems.length, isLoadingSideMenu, fetchSideMenu])

  const handleLogout = () => {
    logout()
    navigate('/auth/login')
  }

  const handleExitImpersonation = async () => {
    if (exitingImpersonation) return
    setExitingImpersonation(true)
    const result = await exitImpersonation()
    if (result.success) {
      toast({
        title: 'Exited client view',
        description: 'You are back in your consultant session.'
      })
      navigate(result.returnPath || '/consultant-clients')
    } else {
      toast({
        title: 'Unable to exit impersonation',
        description: result.error || 'Please try again.',
        variant: 'destructive'
      })
    }
    setExitingImpersonation(false)
  }

  const trialDaysLeft = companyDetails?.is_trial
    ? Math.max(0, (companyDetails.trial_limit_days || 0) - Math.floor((Date.now() - new Date(companyDetails.created_at || Date.now())) / 86_400_000))
    : 0
  const trialUrgent = companyDetails?.is_trial && trialDaysLeft <= 7

  const isActive = (href) => {
    if (/^https?:\/\//i.test(href)) return false
    const [pathPart, searchPart] = String(href || '').split('?')
    const currentPath = location.pathname
    const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search
    if (searchPart) {
      return currentPath === pathPart && currentSearch === searchPart
    }
    // Exact match for dashboard root
    if (pathPart === '/dashboard') {
      return currentPath === '/dashboard'
    }
    
    // For other routes, check if current path matches exactly or starts with the href
    // But make sure we don't match partial segments
    
    // Exact match
    if (currentPath === pathPart) {
      return true
    }
    
    // Check if it starts with the href and the next character is a slash or end of string
    if (currentPath.startsWith(pathPart)) {
      const nextChar = currentPath[pathPart.length]
      return nextChar === '/' || nextChar === undefined
    }
    
    return false
  }

  const displayName = [user?.firstname, user?.lastname].filter(Boolean).join(' ') || user?.name || user?.firstName || user?.username || 'User'
  const roleLabel = user?.role?.name || (typeof user?.role === 'string' ? user.role : '')
  const activeItem = navigationItems.find((item) => isActive(item.href))
  const pageName = activeItem?.name || 'Dashboard'

  // One nav renderer for the desktop rail and the mobile drawer.
  // Design: text-only rows (h34, r8); the active row takes the --accent tint. The icon
  // only appears in the collapsed rail (Settings > Collapsed Sidebar), where it is all there is.
  const renderNav = ({ compact = false, onNavigate } = {}) => {
    if (isLoadingSideMenu) {
      return (
        <div className="flex items-center justify-center py-8 text-sm text-slate-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading menu...
        </div>
      )
    }

    return navigationItems.map((item) => {
      const Icon = getIconComponent(item.icon)
      const active = isActive(item.href)
      const externalTarget = /^https?:\/\//i.test(item.href)
        ? item.href
        : /^https?:\/\//i.test(item.externalUrl || '')
          ? item.externalUrl
          : null
      const rowClass = `flex h-[34px] items-center rounded-lg text-sm transition-colors ${compact ? 'justify-center px-0' : 'px-2.5'} ${
        active
          ? 'bg-accent font-semibold text-blue-800'
          : 'font-normal text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      }`
      const content = compact
        ? <Icon className="h-[18px] w-[18px] flex-shrink-0" />
        : <span className="truncate">{item.name}</span>

      if (externalTarget) {
        return (
          <a
            key={item.name}
            href={externalTarget}
            target="_blank"
            rel="noopener noreferrer"
            title={compact ? item.name : undefined}
            className={rowClass}
            onClick={onNavigate}
          >
            {content}
          </a>
        )
      }

      return (
        <Link
          key={item.name}
          to={item.href}
          title={compact ? item.name : undefined}
          aria-current={active ? 'page' : undefined}
          className={rowClass}
          onClick={onNavigate}
        >
          {content}
        </Link>
      )
    })
  }

  // Signed-in user block at the foot of the sidebar; its chevron opens the account menu upward.
  const renderUserBlock = ({ compact = false, onNavigate, menuRef } = {}) => (
    <div className="relative border-t border-slate-100 p-3" ref={menuRef}>
      <button
        type="button"
        onClick={() => setDropdownOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={dropdownOpen}
        title={compact ? displayName : undefined}
        className={`flex w-full items-center gap-2.5 rounded-lg p-1 text-left transition-colors hover:bg-slate-100 ${compact ? 'justify-center' : ''}`}
      >
        <UserAvatar
          name={displayName}
          email={user?.email}
          src={getUserAvatarUrl(user, userProfile)}
          className="h-[30px] w-[30px] flex-shrink-0"
          neutral
        />
        {!compact && (
          <>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-slate-900">{displayName}</p>
              {roleLabel && <p className="truncate text-[11px] text-slate-500">{roleLabel}</p>}
            </div>
            <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 text-slate-500 transition-transform duration-150 ${dropdownOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {dropdownOpen && (
        <div role="menu" className="absolute bottom-[calc(100%-4px)] left-3 z-50 w-56 rounded-[10px] border border-slate-200 bg-white py-1 shadow-popover">
          <div className="border-b border-slate-100 px-3.5 py-2.5">
            <p className="truncate text-sm font-semibold text-slate-900">{displayName}</p>
            <p className="mt-0.5 truncate text-xs text-slate-500">{user?.email}</p>
          </div>
          <div className="py-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => { navigate('/profile'); setDropdownOpen(false); onNavigate?.() }}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
            >
              <User className="h-4 w-4 text-slate-500" />
              Profile
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => { navigate('/settings'); setDropdownOpen(false); onNavigate?.() }}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
            >
              <Settings className="h-4 w-4 text-slate-500" />
              Settings
            </button>
            {!isClientUser && (
              <button
                type="button"
                role="menuitem"
                onClick={() => { navigate('/billing'); setDropdownOpen(false); onNavigate?.() }}
                className="flex w-full items-center gap-2.5 px-3.5 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
              >
                <CreditCard className="h-4 w-4 text-slate-500" />
                Billing & Plans
              </button>
            )}
          </div>
          <div className="border-t border-slate-100 py-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => { handleLogout(); setDropdownOpen(false) }}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="relative min-h-screen bg-white lg:flex">
      {/* Mobile sidebar backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}
      <aside className={`hidden border-r border-slate-200 bg-background transition-[width] duration-200 lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex lg:flex-col ${collapsed ? 'lg:w-16' : 'lg:w-[248px]'}`}>
        <div className={`flex h-[60px] flex-none items-center border-b border-slate-100 ${collapsed ? 'justify-center' : 'gap-[9px] px-[18px]'}`}>
          <Link to="/dashboard" className="flex items-center gap-[9px]" aria-label="HealthyCoach dashboard">
            <BrandMark />
            {!collapsed && <span className="text-[15px] font-semibold tracking-[-0.015em] text-slate-900">HealthyCoach</span>}
          </Link>
        </div>

        <nav className="custom-scrollbar flex flex-1 flex-col gap-px overflow-y-auto overflow-x-hidden px-2.5 py-3.5" aria-label="Main">
          {renderNav({ compact: collapsed })}
        </nav>

        {renderUserBlock({ compact: collapsed, menuRef: desktopMenuRef })}
      </aside>

      {/* Mobile sidebar */}
      <motion.aside
        initial={false}
        animate={{ x: sidebarOpen ? 0 : '-100%' }}
        transition={{ type: 'tween', duration: 0.2 }}
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-slate-200 bg-background lg:hidden ${sidebarOpen ? 'shadow-popover' : 'invisible'}`}
      >
        <div className="flex h-[60px] flex-none items-center justify-between border-b border-slate-100 px-[18px]">
          <Link to="/dashboard" className="flex items-center gap-[9px]" onClick={() => setSidebarOpen(false)}>
            <BrandMark />
            <span className="text-[15px] font-semibold tracking-[-0.015em] text-slate-900">HealthyCoach</span>
          </Link>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500" onClick={() => setSidebarOpen(false)} aria-label="Close menu">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <nav className="flex flex-1 flex-col gap-px overflow-y-auto px-2.5 py-3.5" aria-label="Main">
          {renderNav({ onNavigate: () => setSidebarOpen(false) })}
        </nav>

        {renderUserBlock({ onNavigate: () => setSidebarOpen(false), menuRef: mobileMenuRef })}
      </motion.aside>

      {/* Main content */}
      <div className={`flex min-h-screen min-w-0 flex-1 flex-col transition-[padding] duration-200 ${collapsed ? 'lg:pl-16' : 'lg:pl-[248px]'}`}>
        {/* Top bar: Company / Page — trial status on the right */}
        <header className="sticky top-0 z-20 flex h-[60px] flex-none items-center gap-2 border-b border-slate-100 bg-white px-4 sm:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-shrink-0 text-slate-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-4 w-4" />
          </Button>

          <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-2">
            {companyDetails?.name && (
              <>
                <span className="hidden max-w-[200px] truncate text-[13px] text-slate-500 sm:block">{companyDetails.name}</span>
                <span className="hidden text-[13px] text-slate-300 sm:block" aria-hidden="true">/</span>
              </>
            )}
            <span className="truncate text-[13px] font-semibold text-slate-900" aria-current="page">{pageName}</span>
          </nav>

          {companyDetails?.is_trial && (
            <span className={`flex flex-shrink-0 items-center gap-2 text-[13px] ${trialUrgent ? 'font-medium text-red-600' : 'text-slate-500'}`}>
              <span className="hidden sm:inline">
                {trialDaysLeft === 0
                  ? 'Trial expired'
                  : `${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} left on trial`}
                {companyDetails.trial_client_limit > 0 && (
                  <span className="hidden md:inline"> · up to {companyDetails.trial_client_limit} users</span>
                )}
              </span>
              <Link to="/billing" className="font-medium text-primary hover:text-primary/80">Upgrade</Link>
            </span>
          )}

          <NewLookToggle />
        </header>

        {isImpersonating && (
          <div className="flex h-auto min-h-[38px] flex-none items-center gap-2.5 bg-slate-900 px-4 py-1.5 sm:px-6">
            <span className="min-w-0 truncate text-[13px] text-white/70">
              Viewing as{' '}
              <span className="font-medium text-white">
                {impersonationMeta?.clientName || impersonationMeta?.clientEmail || 'client'}
              </span>
              {impersonationMeta?.clientName && impersonationMeta?.clientEmail ? ` (${impersonationMeta.clientEmail})` : ''}
            </span>
            <div className="flex-1" />
            <button
              type="button"
              onClick={handleExitImpersonation}
              disabled={exitingImpersonation}
              className="inline-flex h-6 flex-shrink-0 items-center gap-1.5 rounded-md border border-white/[0.22] px-2.5 text-xs font-medium text-white transition-colors hover:bg-white/10 disabled:opacity-60"
            >
              {exitingImpersonation ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              {exitingImpersonation ? 'Exiting…' : 'Exit client view'}
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {animations ? (
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="dashboard-content h-full"
            >
              <div className="p-4 sm:p-6 lg:px-6 lg:py-7">
                <Outlet />
              </div>
            </motion.div>
          ) : (
            <div className="dashboard-content h-full">
              <div className="p-4 sm:p-6 lg:px-6 lg:py-7">
                <Outlet />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default DashboardLayout
